import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import SelectInput, { OptionType } from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { TextInputField } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Box } from '@rebass/grid';
import React, { FC } from 'react';
import { defineMessages, FormattedMessage, IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as Yup from 'yup';
import messages from '../constants/messages';
import { Formable } from '../entities/Formable';
import { LeaveReviewInput, TravelGroupType } from '../generated/graphql';
import Fieldset from './designsystem/Fieldset';
import StyledCaret from './designsystem/StyledCaret';
import Formed, { Handler } from './Formed';
import Stack from './Stack';
import { getReviewOptions } from './ReviewModal';

const travelGroupMessages = defineMessages<TravelGroupType>({
    ALONE: {
        defaultMessage: 'Alleen',
    },
    BUSINESS_TRIP: {
        defaultMessage: 'Zakenreis',
    },
    FAMILY_WITH_OLDER_CHILDREN: {
        defaultMessage: 'Familie met oudere kinderen',
    },
    FAMILY_WITH_YOUNG_CHILDREN: {
        defaultMessage: 'Familie met jongere kinderen',
    },
    GROUP_OF_FRIENDS: {
        defaultMessage: 'Vriendengroep',
    },
    GROUP_TRIP: {
        defaultMessage: 'Groepsreis',
    },
    OLDER_COUPLE: {
        defaultMessage: 'Ouder stel',
    },
    YOUNG_COUPLE: {
        defaultMessage: 'Jong stel',
    },
});

export type Values = Formable<Omit<LeaveReviewInput, 'bookingId'>>;

const getTravelGroupOptions = (intl: IntlShape) =>
    Object.entries(travelGroupMessages).map(
        ([key, value]): OptionType => ({ label: intl.formatMessage(value), value: key })
    );

const initialValues: Values = {
    title: '',
    ratingLocation: '',
    ratingHospitality: '',
    ratingLayout: '',
    ratingMaintenance: '',
    ratingValueForMoney: '',
    comment: '',
    tips: '',
    travelGroupType: '',
};

interface Props {
    handleSubmit: Handler<Values>;
    isCamping: boolean;
}

const BookingReviewForm: FC<React.PropsWithChildren<Props>> = ({ handleSubmit, isCamping = false }) => {
    const intl = useIntl();

    const reviewOptions = getReviewOptions(intl);
    const travelGroupTypeOptions = getTravelGroupOptions(intl);

    const validationSchema = Yup.object().shape({
        title: Yup.string()
            .max(65, intl.formatMessage(messages.maximumCharsError, { maxLength: 65 }))
            .required(intl.formatMessage(messages.fieldError)),
        ratingLocation: Yup.number().required(intl.formatMessage(messages.fieldError)),
        ratingHospitality: Yup.number().required(intl.formatMessage(messages.fieldError)),
        ratingLayout: Yup.number().required(intl.formatMessage(messages.fieldError)),
        ratingMaintenance: Yup.number().required(intl.formatMessage(messages.fieldError)),
        ratingValueForMoney: Yup.number().required(intl.formatMessage(messages.fieldError)),
        comment: Yup.string()
            .max(500, intl.formatMessage(messages.maximumCharsError, { maxLength: 500 }))
            .required(intl.formatMessage(messages.fieldError)),
        tips: Yup.string(),
        travelGroupType: Yup.string().required(intl.formatMessage(messages.fieldError)),
    });

    return (
        <Formed<Values> initialValues={initialValues} validationSchema={validationSchema} handleSubmit={handleSubmit}>
            {({ isSubmitting }) => (
                <>
                    <Box mb={[2, 5]}>
                        <BoldBody>
                            {isCamping ? (
                                <FormattedMessage defaultMessage="Geef s.v.p. een korte zin die de beoordeling van de camping samenvat." />
                            ) : (
                                <FormattedMessage defaultMessage="Geef s.v.p. een korte zin die de beoordeling van je accommodatie samenvat." />
                            )}
                        </BoldBody>
                        <NeutralBody variant="small">
                            <FormattedMessage defaultMessage="Bijvoorbeeld: “Mijn tweede huis”, “Voor herhaling vatbaar”" />
                        </NeutralBody>
                        <SummaryInputField name="title" />
                    </Box>

                    <Box mb={['2rem', 8]}>
                        <Fieldset
                            variant="large"
                            label={<FormattedMessage defaultMessage="Geef aan wat je reisgezelschap was" />}
                            id="travelGroupType"
                        >
                            <TravelGroupTypeSelectInput
                                name="travelGroupType"
                                hideChoices
                                ToggleComponent={StyledCaret}
                                options={travelGroupTypeOptions}
                                placeholder={<FormattedMessage defaultMessage="Kies een reisgezelschap" />}
                            />
                        </Fieldset>
                    </Box>

                    <BoldBody>
                        <FormattedMessage defaultMessage="Beoordeel de volgende eigenschappen met een cijfer" />
                    </BoldBody>

                    <Stack mt="2.4rem" spacing={3} mb={[0, 7]}>
                        <Fieldset
                            variant="large"
                            label={
                                <>
                                    {isCamping ? (
                                        <FormattedMessage defaultMessage="Wat vind je van de ligging van de camping?" />
                                    ) : (
                                        <FormattedMessage defaultMessage="Wat vind je van de ligging van de accommodatie?" />
                                    )}
                                </>
                            }
                            id="ratingLocation"
                        >
                            <ReviewSelectInput
                                name="ratingLocation"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>

                        <Fieldset
                            variant="large"
                            label={
                                <FormattedMessage defaultMessage="Hoe vind je de gastvrijheid van de gastheer/-vrouw?" />
                            }
                            id="ratingHospitality"
                        >
                            <ReviewSelectInput
                                name="ratingHospitality"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>

                        <Fieldset
                            variant="large"
                            label={
                                <>
                                    {isCamping ? (
                                        <FormattedMessage defaultMessage="Wat vind je van de kwaliteit van de plek op de camping die je hebt gekregen?" />
                                    ) : (
                                        <FormattedMessage defaultMessage="Wat vind je van de inrichting (meubels, bedden, servies)?" />
                                    )}
                                </>
                            }
                            id="ratingLayout"
                        >
                            <ReviewSelectInput
                                name="ratingLayout"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>

                        <Fieldset
                            variant="large"
                            label={
                                <>
                                    {isCamping ? (
                                        <FormattedMessage defaultMessage="Hoe vind je de staat van onderhoud van de camping?" />
                                    ) : (
                                        <FormattedMessage defaultMessage="Hoe vind je de staat van onderhoud en de schoonmaak van de accommodatie?" />
                                    )}
                                </>
                            }
                            id="ratingMaintenance"
                        >
                            <ReviewSelectInput
                                name="ratingMaintenance"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>

                        <Fieldset
                            variant="large"
                            label={
                                <>
                                    {isCamping ? (
                                        <FormattedMessage defaultMessage="Hoe beoordeel je de prijs-kwaliteitverhouding van de camping?" />
                                    ) : (
                                        <FormattedMessage defaultMessage="Vind je de prijs en de kwaliteit van de accommodatie in goede verhouding?" />
                                    )}
                                </>
                            }
                            id="ratingValueForMoney"
                        >
                            <ReviewSelectInput
                                name="ratingValueForMoney"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>
                    </Stack>

                    <Box mb={7}>
                        <Body>
                            <FormattedMessage defaultMessage="Toelichting" />
                        </Body>
                        <NeutralBody variant="small">
                            <FormattedMessage defaultMessage="Geef desgewenst een verdere toelichting. Bijvoorbeeld: “alles was schoon”,”de locatie was prachtig”, “ het ontbijt was uitgebreid en lekker”,”de schoonmaak laat te wensen over”. Graag minimaal 1 zin." />
                        </NeutralBody>
                        <TextArea name="comment" />
                    </Box>

                    <Box>
                        <BoldBody>
                            {isCamping ? (
                                <FormattedMessage defaultMessage="Tip voor de gastheer ter verbetering van de camping" />
                            ) : (
                                <FormattedMessage defaultMessage="Tip voor de gastheer ter verbetering van de accommodatie" />
                            )}
                        </BoldBody>
                        <NeutralBody variant="small">
                            <FormattedMessage
                                defaultMessage="Deze tip sturen we direct door naar de gastheer en tonen we niet op de website. {br} Bijvoorbeeld: “het lampje bij het aanrecht werkte niet”, ”de batterij van de klok is leeg”, “ga vooral zo door!”."
                                values={{
                                    br: <br />,
                                }}
                            />
                        </NeutralBody>
                        <TextArea name="tips" />
                    </Box>

                    <PublishButton size="large" type="submit" submitting={isSubmitting}>
                        <FormattedMessage defaultMessage="Beoordeling publiceren" />
                    </PublishButton>
                    <NeutralBody variant="small">
                        <FormattedMessage defaultMessage="Je beoordeling is direct zichtbaar op de website" />
                    </NeutralBody>
                </>
            )}
        </Formed>
    );
};

const NeutralBody = styled(Body)`
    color: ${({ theme }) => theme.colors.neutral['50']};
    margin-bottom: 2rem;
`;

const BoldBody = styled(Body)`
    font-weight: 600;
    margin-bottom: ${({ theme }) => theme.spacing['20_Tiny']};
`;

const PublishButton = styled(Button)`
    margin-bottom: 1rem;
    margin-top: ${({ theme }) => theme.spacing['50_Semi']};
    width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xs}) {
        width: unset;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        margin-top: ${({ theme }) => theme.spacing['80_XXLarge']};
    }
`;

const TextArea = styled(TextInputField).attrs({ type: 'textarea' })`
    min-height: 19.2rem;
`;

const TravelGroupTypeSelectInput = styled(SelectInput)`
    max-width: 60rem;
`;

const ReviewSelectInput = styled(SelectInput)`
    max-width: 20.8rem;
`;

const SummaryInputField = styled(TextInputField)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        min-height: 14.4rem;
    }
`;

export default BookingReviewForm;
